import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Input } from '../components/ui/input';
import SoulBalmLogo from '../components/SoulBalmLogo';
import api from '../lib/api';

const COUNTRY_CODES = [
  { code: '+91', country: 'IN', flag: '\u{1F1EE}\u{1F1F3}' },
  { code: '+1', country: 'US', flag: '\u{1F1FA}\u{1F1F8}' },
  { code: '+44', country: 'UK', flag: '\u{1F1EC}\u{1F1E7}' },
  { code: '+61', country: 'AU', flag: '\u{1F1E6}\u{1F1FA}' },
  { code: '+971', country: 'AE', flag: '\u{1F1E6}\u{1F1EA}' },
  { code: '+65', country: 'SG', flag: '\u{1F1F8}\u{1F1EC}' },
  { code: '+49', country: 'DE', flag: '\u{1F1E9}\u{1F1EA}' },
  { code: '+33', country: 'FR', flag: '\u{1F1EB}\u{1F1F7}' },
  { code: '+81', country: 'JP', flag: '\u{1F1EF}\u{1F1F5}' },
];

export default function CompleteProfilePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    country_code: '+91',
    phone_number: '',
    date_of_birth: '',
    place_of_birth: '',
    time_of_birth: ''
  });

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      const phone = form.phone_number ? `${form.country_code}${form.phone_number}` : null;
      await api.post('/auth/complete-profile', {
        phone,
        date_of_birth: form.date_of_birth || null,
        place_of_birth: form.place_of_birth || null,
        time_of_birth: form.time_of_birth || null
      });
      navigate('/home', { replace: true });
    } catch (e) {
      setError(e.response?.data?.detail || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = form.phone_number.length >= 6 && form.date_of_birth && form.place_of_birth;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#070A1A] px-4 py-8" data-testid="complete-profile-page">
      <div className="absolute inset-0 mystic-gradient" />
      <motion.div className="relative z-10 w-full max-w-md" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <div className="text-center mb-8">
          <SoulBalmLogo size={48} className="mx-auto mb-4" />
          <h1 className="font-['Playfair_Display'] text-2xl text-[#FFFFF0] mb-2">Almost there...</h1>
          <p className="text-sm text-[#BDB2C4]">A few more details to personalize your journey.</p>
        </div>
        <div className="glass-card p-8">
          <div className="space-y-4">
            <div>
              <label className="text-xs text-[#BDB2C4] mb-1 block">Mobile Number</label>
              <div className="flex gap-2">
                <select value={form.country_code} onChange={e => update('country_code', e.target.value)}
                  data-testid="profile-country-code"
                  className="glass-input h-12 rounded-xl px-3 text-[#FFFFF0] bg-white/5 border-white/10 focus:border-[#D4AF37] w-28 appearance-none text-sm">
                  {COUNTRY_CODES.map(c => (
                    <option key={c.code} value={c.code} className="bg-[#0F0B1E]">{c.flag} {c.code}</option>
                  ))}
                </select>
                <Input data-testid="profile-phone" type="tel" placeholder="Phone number" value={form.phone_number}
                  onChange={e => update('phone_number', e.target.value)}
                  className="glass-input h-12 rounded-xl px-4 text-[#FFFFF0] placeholder:text-[#BDB2C4]/50 bg-white/5 border-white/10 focus:border-[#D4AF37] flex-1" />
              </div>
            </div>
            <div>
              <label className="text-xs text-[#BDB2C4] mb-1 block">Date of Birth</label>
              <Input data-testid="profile-dob" type="date" value={form.date_of_birth}
                onChange={e => update('date_of_birth', e.target.value)}
                className="glass-input h-12 rounded-xl px-4 text-[#FFFFF0] bg-white/5 border-white/10 focus:border-[#D4AF37] [color-scheme:dark]" />
            </div>
            <div>
              <label className="text-xs text-[#BDB2C4] mb-1 block">Place of Birth</label>
              <Input data-testid="profile-pob" placeholder="City, Country" value={form.place_of_birth}
                onChange={e => update('place_of_birth', e.target.value)}
                className="glass-input h-12 rounded-xl px-4 text-[#FFFFF0] placeholder:text-[#BDB2C4]/50 bg-white/5 border-white/10 focus:border-[#D4AF37]" />
            </div>
            <div>
              <label className="text-xs text-[#BDB2C4] mb-1 block">Time of Birth</label>
              <Input data-testid="profile-tob" type="time" value={form.time_of_birth}
                onChange={e => update('time_of_birth', e.target.value)}
                className="glass-input h-12 rounded-xl px-4 text-[#FFFFF0] bg-white/5 border-white/10 focus:border-[#D4AF37] [color-scheme:dark]" />
              <p className="text-xs text-[#BDB2C4]/60 mt-1 italic">Optional — helps deepen your reading</p>
            </div>
          </div>
          {error && <p className="text-sm text-[#C87886] mt-4" data-testid="profile-error">{error}</p>}
          <div className="flex justify-between mt-8">
            <button onClick={() => navigate('/home', { replace: true })} className="text-sm text-[#BDB2C4] hover:text-[#D4AF37] transition-colors">
              Skip for now
            </button>
            <button onClick={handleSubmit} disabled={!canSubmit || loading}
              className="btn-primary flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed" data-testid="profile-submit-btn">
              {loading ? 'Saving...' : 'Continue'} <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
